// src/vlp/components/common/UserSelectorDialog.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  CircularProgress,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
  Paper,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsers,
  selectUsersData,
  selectUsersStatus,
  selectUsersError,
} from "../../../redux/usersSlice";

const UserSelectorDialog = ({
  open,
  onClose,
  onSelect,
  multiSelect = false,
  filter = "",
}) => {
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const usersFromStore = useSelector(selectUsersData);
  const usersStatus = useSelector(selectUsersStatus);
  const usersError = useSelector(selectUsersError);

  const [search, setSearch] = useState("");
  const [selectedOperators, setSelectedOperators] = useState([]);

  // Normalize - Updated for UsersDto structure (camelCase)
  const getOperator = (u) => u?.operator ?? "";
  const getAliasName = (u) => u?.aliasName ?? "";
  const getTitle = (u) => u?.title ?? "";
  const getEmail = (u) => u?.email ?? "";
  const getFullName = (u) => u?.fullName ?? "";
  const getUserGroup = (u) => u?.userGroup ?? "";

  // Load data
  useEffect(() => {
    if (open && usersStatus === "idle") {
      dispatch(fetchUsers());
    }
  }, [open, usersStatus, dispatch]);

    useEffect(() => {
    if (open) {
      setSelectedOperators([]);
      setSearch("");
      // Focus the search box after a short delay to ensure Dialog is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedOperators([]);
      setSearch("");
    }
  }, [open]);

  // Filter first, then limit to 20
  const filteredUsers = useMemo(() => {
    let list = usersFromStore || [];    
    // 1. Apply group filter if provided
    if (filter.trim()) {
      const group = filter.toLowerCase().trim();
      list = list.filter((u) => getUserGroup(u).toLowerCase().includes(group));
    }

    // 2. Apply search filter
    if (search.trim()) {
      const term = search.toLowerCase().trim();

      list = list.filter((u) => {
        const operator = getOperator(u).toLowerCase();
        const aliasName = getAliasName(u).toLowerCase();
        const title = getTitle(u).toLowerCase();
        const email = getEmail(u).toLowerCase();
        const fullName = getFullName(u).toLowerCase();

        return (
          operator.includes(term) ||
          aliasName.includes(term) ||
          title.includes(term) ||
          email.includes(term) ||
          fullName.includes(term)
        );
      });
    }

    // 3. Limit to 20
    return list.slice(0, 20);
  }, [usersFromStore, search, filter]);

  const toggleSelection = (operator) => {
    if (!multiSelect) {
      setSelectedOperators([operator]);
      // Auto-select and close for single select
      const user = (usersFromStore || []).find(
        (u) => getOperator(u) === operator
      );
      onSelect(user || null);
      onClose();
      return;
    }
    setSelectedOperators((prev) =>
      prev.includes(operator)
        ? prev.filter((o) => o !== operator)
        : [...prev, operator]
    );
  };

  const handleConfirm = () => {
    if (multiSelect) {
      const selectedUsers = (usersFromStore || []).filter((u) =>
        selectedOperators.includes(getOperator(u))
      );
      onSelect(selectedUsers);
    } else {
      const user = (usersFromStore || []).find(
        (u) => getOperator(u) === selectedOperators[0]
      );
      onSelect(user || null);
    }
    onClose();
  };

  const removeSelectedOperator = (operatorToRemove) => {
    setSelectedOperators((prev) =>
      prev.filter((operator) => operator !== operatorToRemove)
    );
  };

  const getUserInitials = (user) => {
    const aliasName = getAliasName(user);
    const fullName = getFullName(user);
    const operator = getOperator(user);

    if (aliasName) {
      return aliasName.substring(0, 2).toUpperCase();
    } else if (fullName) {
      const names = fullName.split(" ");
      return names.length > 1
        ? (names[0][0] + names[1][0]).toUpperCase()
        : names[0].substring(0, 2).toUpperCase();
    } else if (operator) {
      return operator.substring(0, 2).toUpperCase();
    }
    return "US";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "60vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          background: "linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <PersonIcon />
        {multiSelect ? "Select Users" : "Select User"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {usersError && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "error.lighter",
              border: "1px solid",
              borderColor: "error.light",
              borderRadius: 2,
            }}
          >
            <Typography color="error" variant="body2">
              {usersError}
            </Typography>
          </Paper>
        )}

        <TextField
          value={search}
          inputRef={searchInputRef}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          placeholder="Search by operator, alias name, title, email, or full name..."
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "grey.50",
              height: "40px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Debug info - remove this after testing */}
        {search.trim() && (
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, color: "warning.main" }}
          >
            Searching for "{search}" - Found {filteredUsers.length} results
          </Typography>
        )}

        {/* Selected items chips for multiSelect */}
        {multiSelect && selectedOperators.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Selected ({selectedOperators.length}):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedOperators.map((operator) => (
                <Chip
                  key={operator}
                  label={operator}
                  onDelete={() => removeSelectedOperator(operator)}
                  deleteIcon={<CloseIcon />}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {usersStatus === "loading" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                Loading users...
              </Typography>
            </Box>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">No results found</Typography>
            <Typography variant="body2">
              Try adjusting your search terms
            </Typography>
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            <List dense sx={{ p: 0 }}>
              {filteredUsers.map((u, index) => {
                const operator = getOperator(u);
                const aliasName = getAliasName(u);
                const title = getTitle(u);
                const email = getEmail(u);
                const isSelected = selectedOperators.includes(operator);

                return (
                  <React.Fragment key={operator}>
                    <ListItem
                      disablePadding
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => toggleSelection(operator)}
                        sx={{
                          py: 1,
                          px: 2,
                          minHeight: "48px",
                          ...(isSelected && {
                            bgcolor: "secondary.lighter",
                            "&:hover": {
                              bgcolor: "secondary.light",
                            },
                          }),
                        }}
                      >
                        {multiSelect && (
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                              color="secondary"
                              size="small"
                            />
                          </ListItemIcon>
                        )}
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 2,
                            bgcolor: isSelected ? "secondary.main" : "grey.400",
                            fontSize: "0.75rem",
                          }}
                        >
                          {getUserInitials(u)}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: isSelected ? 700 : 600,
                                  color: isSelected
                                    ? "secondary.main"
                                    : "#7b1fa2",
                                  fontSize: "0.9rem",
                                }}
                              >
                                {operator}
                              </Typography>
                              {title && (
                                <Chip
                                  label={title}
                                  size="small"
                                  color="info"
                                  variant="filled"
                                  icon={
                                    <WorkIcon sx={{ fontSize: "0.7rem" }} />
                                  }
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    "& .MuiChip-label": {
                                      px: 1,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 0.5,
                                  fontWeight: 600,
                                  color: isSelected ? "#7b1fa2" : "#1a1a1a",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {aliasName}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                {email && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <EmailIcon
                                      sx={{ fontSize: "0.8rem", color: "#666" }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#1976d2",
                                        fontWeight: 500,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {email}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredUsers.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}

        {filteredUsers.length === 20 && search.trim() === "" && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block", textAlign: "center" }}
          >
            Showing first 20 results. Use search to narrow down.
          </Typography>
        )}
        {filteredUsers.length === 20 && search.trim() !== "" && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{
              mt: 1,
              display: "block",
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            {filteredUsers.length}+ results found. Use more specific search
            terms to narrow down.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        {multiSelect && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={selectedOperators.length === 0}
            sx={{
              borderRadius: 2,
              px: 3,
              background: "linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)",
            }}
          >
            Select ({selectedOperators.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserSelectorDialog;
